import { Button, Text } from "@asayinc/component-library";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { OpenInNew } from "@mui/icons-material";

import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import {
  LoadingCard,
  LoadingStatsHeaderCard,
} from "src/components/molecules/Loading";
import { useAdditionalMailingQuery } from "src/store/additionalMailing";
import {
  ADDITIONAL_MAILING_TYPE_LABEL,
  ADDITIONAL_MAILING_AUDIENCE_LABEL,
} from "src/constants/labels/additionalMailingLabel";
import {
  AdditionalMailingType,
  AdditionalMailingAudience,
} from "src/store/additionalMailing/types";

export function AdditionalMailingDetailsTab() {
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };
  const {
    data: additionalMailing,
    isLoading,
    error,
  } = useAdditionalMailingQuery({ id });

  if (error || (!isLoading && !additionalMailing)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <LoadingStatsHeaderCard />
        <Box sx={{ display: "flex", mt: 8 }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
            <LoadingCard />
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const {
    type,
    securities,
    dateReceived,
    audience,
    minimumShares,
    jobNumber,
    proxyEvent,
  } = additionalMailing;

  const typeLabel =
    ADDITIONAL_MAILING_TYPE_LABEL[type as AdditionalMailingType] || type;
  const audienceLabel =
    ADDITIONAL_MAILING_AUDIENCE_LABEL[audience as AdditionalMailingAudience] ||
    audience;

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Card title="Additional Mailing Details">
        <Stack sx={{ mt: 6, px: 6 }} direction="row">
          <Box sx={{ width: "20%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Type
            </Text>
            <Text variant="h5">{typeLabel}</Text>
          </Box>
          <Box sx={{ width: "20%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              CUSIPs
            </Text>
            <Text variant="h5">
              {securities.map((security) => security.cusip).join(", ")}
            </Text>
          </Box>
          <Box sx={{ width: "20%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Record Date
            </Text>
            <Text variant="h5">
              {dayjs(proxyEvent.recordDate).format("MM/DD/YYYY")}
            </Text>
          </Box>
          <Box sx={{ width: "20%" }}>
            <Text variant="subtitle1" sx={{ mb: 3 }}>
              Job Number
            </Text>
            <Text variant="h5">{jobNumber || "-"}</Text>
          </Box>
        </Stack>
      </Card>
      <Box sx={{ display: "flex", mt: 8 }}>
        <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
          <Card title="Additional Mailing Information">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Date Received"
              value={dayjs(dateReceived).format("MMMM DD, YYYY")}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Audience"
              value={audienceLabel}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Minimum Shares"
              value={minimumShares.toString()}
            />
          </Card>
        </Box>
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          <Card title="Proxy Event Information">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Proxy Event Job Number"
              isLabelBold={true}
              value={proxyEvent.jobNumber || "-"}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Record Date"
              isLabelBold={true}
              value={dayjs(proxyEvent.recordDate).format("MMMM DD, YYYY")}
            />
            {proxyEvent.meetingDate && (
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  borderBottom: "1px solid #e0e0e0",
                }}
                label="Meeting Date"
                isLabelBold={true}
                value={dayjs(proxyEvent.meetingDate).format("MMMM DD, YYYY")}
              />
            )}
            {proxyEvent.voteCutoff && (
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  borderBottom: "1px solid #e0e0e0",
                }}
                label="Vote Cutoff"
                isLabelBold={true}
                value={dayjs(proxyEvent.voteCutoff).format(
                  "MMMM DD, YYYY hh:mma"
                )}
              />
            )}
            <Box
              sx={{ py: 5, px: 4, display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="secondary"
                size="small"
                endIcon={<OpenInNew />}
                onClick={() =>
                  navigate(`/proxy-events/${proxyEvent.id}/details`)
                }
              >
                View Proxy Event
              </Button>
            </Box>
          </Card>
        </Box>
      </Box>
    </ScrollContainer>
  );
}
